import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    FileJsonIcon,
    GripVerticalIcon,
    PencilIcon,
    PlusIcon,
    SaveIcon,
    Trash2Icon,
    UploadIcon,
} from "lucide-react";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {useNavigate, useOutletContext, useSearchParams} from "react-router-dom";
import {useParams} from "react-router";
import SectionHeading from "../../../../../components/SectionHeading";
import {useAuth} from "../../../../../context/AuthContext";
import {ROLES} from "../../../../../routing/guards";
import SponsorImportModal from "../components/SponsorImportModal";

type OutletCtx = { eventId: string | null };

type SponsorTier = "TITLE" | "GOLD" | "SILVER" | "BRONZE" | "PARTNER";

type SponsorForm = {
    id?: string;
    name: string;
    logoUrl: string;
    websiteUrl?: string;
    tier: SponsorTier;
    position?: number;
    active: boolean;
};

function cx(...c: Array<string | false | null | undefined>) {
    return c.filter(Boolean).join(" ");
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

function normalizePositions(list: SponsorForm[]): SponsorForm[] {
    return list.map((s, idx) => ({...s, position: idx + 1}));
}

function emptySponsor(): SponsorForm {
    return {
        name: "",
        logoUrl: "",
        websiteUrl: "",
        tier: "PARTNER",
        position: 0,
        active: true,
    };
}

async function upsertSponsorMultipart(
    businessId: string,
    eventId: string,
    dto: any,
    file?: File | null
): Promise<SponsorForm[]> {
    const fd = new FormData();
    fd.append("dto", new Blob([JSON.stringify(dto)], {type: "application/json"}));
    if (file) fd.append("file", file);

    const {data} = await api.patch(
        `/api/v1/business/${businessId}/events/${eventId}/sponsors`,
        fd,
        {headers: {"Content-Type": "multipart/form-data"}}
    );

    const raw = Array.isArray(data) ? data : (data?.sponsors ?? []);
    const mapped: SponsorForm[] = (raw ?? []).map((x: any) => ({
        id: x?.id ? String(x.id) : undefined,
        name: x?.name ?? "",
        logoUrl: x?.logoUrl ?? "",
        websiteUrl: x?.websiteUrl ?? "",
        tier: (x?.tier ?? "PARTNER") as SponsorTier,
        position: x?.position != null ? String(x.position) : 0,
        active: Boolean(x?.active ?? true),
    }));

    const sorted = [...mapped].sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
    return normalizePositions(sorted);
}

function SponsorEditor({
                           value,
                           onChange,
                           onSave,
                           onCancel,
                           saving,
                           uploadingLogo,
                       }: {
    value: SponsorForm;
    onChange: (next: SponsorForm) => void;
    onSave: (logoFile: File | null) => void;
    onCancel: () => void;
    saving: boolean;
    uploadingLogo: boolean;
}) {
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const hasLogo = (value.logoUrl?.trim()?.length ?? 0) > 0 || !!logoFile;

    const canSave =
        value.name.trim().length > 0 &&
        hasLogo &&
        !saving &&
        !uploadingLogo;

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {value.id ? "Edit sponsor" : "New sponsor"}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Sponsors appear on the event page (and can be used for sponsor bars, posters, etc.).
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" size="md" onClick={onCancel}
                            disabled={saving || uploadingLogo}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => onSave(logoFile)}
                        disabled={!canSave}
                    >
                        {saving || uploadingLogo ? "Saving…" : (
                            <span className="inline-flex items-center gap-2">
                            <SaveIcon className="size-4"/>
                            Save sponsor
                          </span>
                        )}
                    </Button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-900">Sponsor name *</label>
                        <input
                            value={value.name}
                            onChange={(e) => onChange({...value, name: e.target.value})}
                            className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                            placeholder="e.g. Acme Corp"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Logo *</label>
                        <p className="mt-1 text-xs text-gray-500">
                            Upload a PNG/JPG/SVG. We’ll store it and save the URL.
                        </p>

                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0] ?? null;
                                setLogoFile(f);
                                e.currentTarget.value = "";
                            }}
                        />

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="md"
                                disabled={saving || uploadingLogo}
                                onClick={() => fileRef.current?.click()}
                                leftIcon={<UploadIcon className="size-4" aria-hidden="true"/>}
                            >
                                {logoFile ? "Logo selected" : (value.logoUrl ? "Change logo" : "Upload logo")}
                            </Button>

                            {logoFile ? (
                                <button
                                    type="button"
                                    className="text-sm font-medium text-gray-600 hover:text-gray-900"
                                    disabled={saving || uploadingLogo}
                                    onClick={() => setLogoFile(null)}
                                >
                                    Clear selected file
                                </button>
                            ) : null}

                            {value.logoUrl ? (
                                <button
                                    type="button"
                                    className="text-sm font-medium text-gray-600 hover:text-gray-900"
                                    disabled={saving || uploadingLogo}
                                    onClick={() => onChange({...value, logoUrl: ""})}
                                >
                                    Remove existing URL
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Website (optional)</label>
                        <input
                            value={value.websiteUrl ?? ""}
                            onChange={(e) => onChange({...value, websiteUrl: e.target.value})}
                            className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                            placeholder="https://www.example.com"
                        />
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm font-semibold text-gray-900">Preview</div>
                        <div className="mt-3 overflow-hidden rounded-lg bg-white ring-1 ring-black/5">
                            {logoFile ? (
                                <img src={URL.createObjectURL(logoFile)} alt=""
                                     className="h-32 w-full object-contain p-3"/>
                            ) : value.logoUrl ? (
                                <img src={value.logoUrl} alt="" className="h-32 w-full object-contain p-3"/>
                            ) : (
                                <div className="flex h-32 items-center justify-center text-sm text-gray-500">
                                    Logo preview
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Tier</label>
                        <select
                            value={value.tier}
                            onChange={(e) => onChange({...value, tier: e.target.value as SponsorTier})}
                            className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                        >
                            <option value="TITLE">Title</option>
                            <option value="GOLD">Gold</option>
                            <option value="SILVER">Silver</option>
                            <option value="BRONZE">Bronze</option>
                            <option value="PARTNER">Partner</option>
                        </select>
                    </div>

                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={value.active}
                            onChange={(e) => onChange({...value, active: e.target.checked})}
                            className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                        />
                        <div>
                            <div className="text-sm font-medium text-gray-900">Active</div>
                            <div className="text-xs text-gray-500">Inactive sponsors are hidden on the event page.</div>
                        </div>
                    </label>
                </div>
            </div>
        </div>
    );
}

export function SponsorsStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [dirtyOrder, setDirtyOrder] = useState(false);

    const [sponsors, setSponsors] = useState<SponsorForm[]>([]);
    const [editor, setEditor] = useState<SponsorForm | null>(null);
    const [importOpen, setImportOpen] = useState(false);

    const dragFrom = useRef<number | null>(null);

    const {user} = useAuth();
    // Platform administrators only. Not useBusinessAccess().isAdmin, which is also true for a
    // BUSINESS_ADMIN and would open the import to every company's own admins.
    const isAdmin = !!user?.roles?.includes(ROLES.ADMIN);

    /** Named rather than inlined in the effect, so the JSON import can refresh the list too. */
    const loadSponsors = useCallback(async () => {
        if (!businessId || !eventId) return;

        setLoading(true);
        try {
            const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/sponsors`);
            const raw = Array.isArray(data) ? data : (data?.sponsors ?? []);
            const mapped: SponsorForm[] = (raw ?? []).map((x: any) => ({
                id: x?.id ? String(x.id) : undefined,
                name: x?.name ?? "",
                logoUrl: x?.logoUrl ?? "",
                websiteUrl: x?.websiteUrl ?? "",
                tier: (x?.tier ?? "PARTNER") as SponsorTier,
                position: x?.position != null ? String(x.position) : "0",
                active: Boolean(x?.active ?? true),
            }));

            const sorted = [...mapped].sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
            setSponsors(normalizePositions(sorted));
            setDirtyOrder(false);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load sponsors"));
        } finally {
            setLoading(false);
        }
    }, [businessId, eventId]);

    useEffect(() => {
        void loadSponsors();
    }, [loadSponsors]);

    const openNew = () => setEditor(emptySponsor());
    const openEdit = (s: SponsorForm) => setEditor({...s});

    const deleteSponsor = async (s: SponsorForm) => {
        if (!businessId || !eventId) return;

        if (!s.id) {
            setSponsors((prev) => normalizePositions(prev.filter((x) => x !== s)));
            setDirtyOrder(true);
            return;
        }

        if (!confirm(`Delete sponsor "${s.name}"?`)) return;

        try {
            await api.delete(`/api/v1/business/${businessId}/events/${eventId}/sponsors/${s.id}`);
            setSponsors((prev) => normalizePositions(prev.filter((x) => x.id !== s.id)));
            setDirtyOrder(true);
            notify.success("Sponsor deleted");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete sponsor"));
        }
    };

    const saveOrder = async () => {
        if (!businessId || !eventId) return;
        setSaving(true);
        try {
            const normalized = normalizePositions(sponsors);

            const payload = normalized
                .filter(s => !!s.id)
                .map(s => ({
                    id: s.id,
                    position: Number(s.position ?? 0),
                }));

            const {data} = await api.patch(
                `/api/v1/business/${businessId}/events/${eventId}/sponsors/reorder`,
                payload
            );

            // map back like you already do
            const raw = Array.isArray(data) ? data : (data?.sponsors ?? []);
            const mapped: SponsorForm[] = (raw ?? []).map((x: any) => ({
                id: x?.id ? String(x.id) : undefined,
                name: x?.name ?? "",
                logoUrl: x?.logoUrl ?? "",
                websiteUrl: x?.websiteUrl ?? "",
                tier: (x?.tier ?? "PARTNER") as SponsorTier,
                position: x?.position != null ? String(x.position) : 0,
                active: Boolean(x?.active ?? true),
            }));

            setSponsors(normalizePositions(mapped));
            setDirtyOrder(false);
            notify.success("Sponsor order saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save sponsor order"));
        } finally {
            setSaving(false);
        }
    };

    const saveEditor = async (logoFile: File | null) => {
        if (!businessId || !eventId || !editor) return;

        setUploadingLogo(true);
        try {
            const dto = {
                id: editor.id ?? null,
                name: editor.name?.trim(),
                logoUrl: editor.logoUrl?.trim() || null,
                websiteUrl: editor.websiteUrl?.trim() || null,
                tier: editor.tier,
                position: editor.position ? Number(editor.position) : null,
                active: Boolean(editor.active),
            };

            const updated = await upsertSponsorMultipart(businessId, eventId, dto, logoFile);
            setSponsors(updated);
            setEditor(null);
            setDirtyOrder(false);
            notify.success("Sponsor saved");
        } catch (e: any) {
            notify.error(getErrMsg(e, "Failed to save sponsor"));
        } finally {
            setUploadingLogo(false);
        }
    };

    const onDragStart = (idx: number) => {
        dragFrom.current = idx;
    };
    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };
    const onDrop = (toIdx: number) => {
        const fromIdx = dragFrom.current;
        dragFrom.current = null;
        if (fromIdx == null || fromIdx === toIdx) return;

        setSponsors((prev) => {
            const moved = moveItem(prev, fromIdx, toIdx);
            return normalizePositions(moved);
        });
        setDirtyOrder(true);
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/speakers`);
        else navigate(`/business/events/new/speakers?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    if (editor) {
        return (
            <SponsorEditor
                value={editor}
                onChange={setEditor}
                onSave={saveEditor}
                onCancel={() => setEditor(null)}
                saving={saving}
                uploadingLogo={uploadingLogo}
            />
        );
    }

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit event sponsors" : "Add event sponsors"}
                description="Sponsors appear on the event page (and can be used for sponsor bars, posters, etc.)."
                actions={
                    <>
                        {dirtyOrder ? (
                            <Button type="button" variant="secondary" size="md" onClick={saveOrder} disabled={saving}>
                                {saving ? "Saving…" : (
                                    <span className="inline-flex items-center gap-2">
                                                <SaveIcon className="size-4"/>
                                                Save order
                                            </span>
                                )}
                            </Button>
                        ) : null}

                        {isAdmin ? (
                            <Button type="button" variant="secondary" size="md" onClick={() => setImportOpen(true)}>
                                <span className="inline-flex items-center gap-2">
                                    <FileJsonIcon className="size-4"/>
                                    Import JSON
                                </span>
                            </Button>
                        ) : null}

                        <Button type="button" variant="primary" size="md" onClick={openNew}>
                            <span className="inline-flex items-center gap-2">
                                <PlusIcon className="size-4"/>
                                Add sponsor
                            </span>
                        </Button>
                    </>
                }
            />

            {isAdmin && eventId ? (
                <SponsorImportModal
                    open={importOpen}
                    onClose={() => setImportOpen(false)}
                    eventId={eventId}
                    onImported={loadSponsors}
                />
            ) : null}

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading sponsors…</div>
            ) : (
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
                        <div className="grid grid-cols-12 gap-3 text-xs font-semibold text-gray-600">
                            <div className="col-span-6">Sponsor</div>
                            <div className="col-span-2">Tier</div>
                            <div className="col-span-2">Status</div>
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                    </div>

                    <ul className="divide-y divide-gray-100">
                        {sponsors.length === 0 ? (
                            <li className="px-5 py-10 text-center text-sm text-gray-500">
                                No sponsors yet. Click <span className="font-medium">Add sponsor</span>.
                            </li>
                        ) : sponsors.map((s, idx) => (
                            <li
                                key={s.id ?? `new-${idx}`}
                                draggable
                                onDragStart={() => onDragStart(idx)}
                                onDragOver={onDragOver}
                                onDrop={() => onDrop(idx)}
                                className="px-5 py-4 hover:bg-gray-50/60"
                            >
                                <div className="grid grid-cols-12 items-center gap-3">
                                    <div className="col-span-6 flex items-center gap-3">
                                        <div className="flex items-center gap-2 text-gray-500">
                                            <GripVerticalIcon className="size-4"/>
                                            <span className="text-xs">{s.position}</span>
                                        </div>

                                        <div
                                            className="size-10 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/5">
                                            {s.logoUrl ? (
                                                <img src={s.logoUrl} alt="" className="size-10 object-contain p-1"/>
                                            ) : null}
                                        </div>

                                        <div className="min-w-0">
                                            <div
                                                className="truncate text-sm font-semibold text-gray-900">{s.name || "Untitled"}</div>
                                            {s.websiteUrl ? (
                                                <div className="truncate text-xs text-gray-500">{s.websiteUrl}</div>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="col-span-2 text-sm text-gray-700">{s.tier}</div>

                                    <div className="col-span-2">
                    <span className={cx(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                        s.active ? "bg-emerald-50 text-emerald-700 ring-emerald-100" : "bg-gray-100 text-gray-700 ring-gray-200"
                    )}>
                      {s.active ? "Active" : "Hidden"}
                    </span>
                                    </div>

                                    <div className="col-span-2 flex items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => openEdit(s)}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                        >
                                            <PencilIcon className="size-4"/>
                                            <span className="hidden sm:inline">Edit</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => deleteSponsor(s)}
                                            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2Icon className="size-4"/>
                                            <span className="hidden sm:inline">Delete</span>
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate("/business/events")}>
                    Exit
                </Button>

                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving || uploadingLogo}>
                    Continue to Speakers
                </Button>
            </div>
        </div>
    );
}
